import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { runGeneration } from "@/lib/generate";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGE_GROUPS, LANGUAGES, label } from "@/lib/activityData";

const CENTERS = [
  { en: "Dramatic Play", es: "Juego dramático" }, { en: "Blocks", es: "Bloques" },
  { en: "Library", es: "Biblioteca" }, { en: "Writing", es: "Escritura" },
  { en: "Art", es: "Arte" }, { en: "Math/Manipulatives", es: "Matemáticas/Manipulativos" },
  { en: "Science/Discovery", es: "Ciencia/Descubrimiento" }, { en: "Sensory", es: "Sensorial" },
  { en: "Music", es: "Música" }, { en: "Technology", es: "Tecnología" },
  { en: "Outdoor Learning", es: "Aprendizaje al aire libre" },
];

export default function LearningCenters() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [center, setCenter] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [theme, setTheme] = useState("");
  const [language, setLanguage] = useState("bilingual");
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const submit = async () => {
    if (!center || !ageGroup) { toast.error(lang === "es" ? "Elige un centro y grupo de edad." : "Choose a center and age group."); return; }
    setLoading(true); setElapsed(0);
    try {
      const j = await runGeneration("learning-center", { center, age_group: ageGroup, theme, language }, { onTick: setElapsed });
      toast.success(t("activity.saved"));
      navigate(`/activities/${j.result_id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <GeneratingOverlay open={loading} elapsed={elapsed} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav.dashboard")}
        </button>
        <div className="space-y-2">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Entrenador de Centros de Aprendizaje" : "Learning Center Coach"}</h1>
          <p className="text-muted-foreground text-lg">{lang === "es" ? "Genera una actividad bilingüe para cualquier centro." : "Generate a bilingual activity for any classroom center."}</p>
        </div>
        <div className="space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="space-y-2">
            <Label>{lang === "es" ? "Centro" : "Center"} <span className="text-secondary">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {CENTERS.map((c) => (
                <button key={c.en} onClick={() => setCenter(c.en)} data-testid={`center-${c.en.replace(/\W+/g, "-")}`}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${center === c.en ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {label(c, lang)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("generator.ageGroup")} <span className="text-secondary">*</span></Label>
              <Select value={ageGroup} onValueChange={setAgeGroup}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="lc-age"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("generator.language")}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="lc-lang"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{label(l, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("generator.theme")}</Label>
            <Input value={theme} onChange={(e) => setTheme(e.target.value)} placeholder={t("generator.themePlaceholder")} className="h-12 rounded-xl" data-testid="lc-theme" />
          </div>
          <Button onClick={submit} disabled={loading} className="w-full py-3 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="lc-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("generator.generating")}</> : <><LayoutGrid className="h-5 w-5" /> {t("common.generate")}</>}
          </Button>
        </div>
      </main>
    </div>
  );
}
